import { sourceControlToggleAtom } from '@/atoms/sourceControlAtoms';;
import { applyChatSourceToggleGuard } from '@/atoms/chatSourceRequireOne';
import { useIsEvidenceChatRequireAtLeastOneSourceEnabled } from '@/features/flags/evidence-chat-require-at-least-one-source';
import { useExpandedSourceControlTrackingEnabled } from '@/features/flags';
import { invalidateGamifyTrialExtension } from '@/hooks/query/useQueryGamifyTrialExtension';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import type { EvidenceBaseEventProps } from '@/models/analytics/features/evidence';
import { ButtonAction } from '@heidi/runtime/analytics-vocabulary';
import { recordGamifySourceControlUsed } from '@/pages/api/scribeAPIV2';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useRef } from 'react';

type CollectionTrackingContext = {
  name?: string;
  sources?: string[];
  source_ids?: string[];
  sources_count?: number;
};

type PartnerTrackingContext = {
  displayName?: string;
};

export const useSourceControlActions = (baseProps: EvidenceBaseEventProps) => {
  const hasRecordedRef = useRef(false);
  const recordSourceControlUsage = useCallback(() => {
    if (hasRecordedRef.current) return;
    hasRecordedRef.current = true;
    void recordGamifySourceControlUsed()
      .then(() => invalidateGamifyTrialExtension())
      .catch(() => {});
  }, []);

  const toggleState = useAtomValue(sourceControlToggleAtom);
  const setToggleState = useSetAtom(sourceControlToggleAtom);
  const isRequireAtLeastOneSource = useIsEvidenceChatRequireAtLeastOneSourceEnabled();

  const setToggleStateGuarded = useCallback(
    (
      updater:
        | typeof toggleState
        | ((prev: typeof toggleState) => typeof toggleState)
    ) => {
      setToggleState((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        return applyChatSourceToggleGuard(prev, next, isRequireAtLeastOneSource);
      });
    },
    [setToggleState, isRequireAtLeastOneSource]
  );

  const isExpandedSourceTrackingEnabled = useExpandedSourceControlTrackingEnabled();

  const trackCollectionToggle = useCallback(
    (
      isEnabled: boolean,
      context?: CollectionTrackingContext,
      label = 'Collection toggle'
    ) => {
      if (!isExpandedSourceTrackingEnabled) return;
      track({
        event_name: TrackingEventName.ButtonClicked,
        props: {
          ...baseProps,
          action: ButtonAction.EvidenceSourceSelected,
          label: context?.name ?? label,
          is_enabled: isEnabled,
          feature: 'evidence',
          library_name: context?.name,
          library_sources: context?.sources,
          library_source_ids: context?.source_ids,
          library_sources_count: context?.sources_count,
        },
      });
    },
    [isExpandedSourceTrackingEnabled, baseProps]
  );

  const handleDocCollectionToggle = useCallback(
    (collectionId: string, context?: CollectionTrackingContext) => {
      recordSourceControlUsage();
      const isCurrentlySelected =
        toggleState.selectedCollectionIds.includes(collectionId);
      trackCollectionToggle(!isCurrentlySelected, context, 'Library collection');
      setToggleStateGuarded((prev) => {
        const currentIds = prev.selectedCollectionIds;
        const newIds = currentIds.includes(collectionId)
          ? currentIds.filter((id) => id !== collectionId)
          : [...currentIds, collectionId];

        // Deselecting last collection: disable library unless uncollected is still on
        if (newIds.length === 0 && prev.myLibrary && currentIds.length > 0) {
          if (prev.includeUncollected) {
            return {
              ...prev,
              selectedCollectionIds: [],
              libraryMode: 'all' as const,
            };
          }
          return {
            ...prev,
            myLibrary: false,
            libraryMode: 'all' as const,
            selectedCollectionIds: [],
            includeUncollected: false,
          };
        }

        return {
          ...prev,
          selectedCollectionIds: newIds,
          libraryMode: newIds.length > 0 ? ('collections' as const) : ('all' as const),
          // Auto-enable library when selecting first collection
          ...(newIds.length > 0 && !prev.myLibrary ? { myLibrary: true } : {}),
        };
      });
    },
    [
      setToggleStateGuarded,
      recordSourceControlUsage,
      toggleState.selectedCollectionIds,
      trackCollectionToggle,
    ]
  );

  const handleWebCollectionToggle = useCallback(
    (collectionId: string, context?: CollectionTrackingContext) => {
      recordSourceControlUsage();
      const isCurrentlySelected =
        toggleState.selectedWebCollectionIds.includes(collectionId);
      trackCollectionToggle(!isCurrentlySelected, context, 'Web collection');
      setToggleStateGuarded((prev) => {
        const currentIds = prev.selectedWebCollectionIds;
        const newIds = currentIds.includes(collectionId)
          ? currentIds.filter((id) => id !== collectionId)
          : [...currentIds, collectionId];

        // Deselecting last collection: disable web collections entirely
        if (newIds.length === 0 && prev.webCollections && currentIds.length > 0) {
          return {
            ...prev,
            webCollections: false,
            webCollectionsMode: 'all' as const,
            selectedWebCollectionIds: [],
          };
        }

        return {
          ...prev,
          selectedWebCollectionIds: newIds,
          webCollectionsMode: newIds.length > 0 ? ('collections' as const) : ('all' as const),
          // Auto-enable web collections when selecting first collection
          ...(newIds.length > 0 && !prev.webCollections ? { webCollections: true } : {}),
        };
      });
    },
    [
      setToggleStateGuarded,
      recordSourceControlUsage,
      toggleState.selectedWebCollectionIds,
      trackCollectionToggle,
    ]
  );

  const handleUncollectedToggle = useCallback(() => {
    recordSourceControlUsage();
    setToggleStateGuarded((prev) => {
      const newValue = !prev.includeUncollected;
      if (newValue) {
        return {
          ...prev,
          includeUncollected: true,
          ...(prev.myLibrary ? {} : { myLibrary: true }),
        };
      }
      return {
        ...prev,
        includeUncollected: false,
        ...(prev.selectedCollectionIds.length === 0
          ? { myLibrary: false, libraryMode: 'all' as const }
          : {}),
      };
    });
  }, [setToggleStateGuarded, recordSourceControlUsage]);

  const isAllLibrarySelected =
    (toggleState.myLibrary || toggleState.webCollections) &&
    toggleState.selectedCollectionIds.length === 0 &&
    toggleState.selectedWebCollectionIds.length === 0;

  const handleAllLibraryToggle = useCallback(() => {
    recordSourceControlUsage();
    setToggleStateGuarded((prev) => {
      const allSelected =
        (prev.myLibrary || prev.webCollections) &&
        prev.selectedCollectionIds.length === 0 &&
        prev.selectedWebCollectionIds.length === 0;

      if (allSelected) {
        return {
          ...prev,
          myLibrary: false,
          libraryMode: 'all' as const,
          selectedCollectionIds: [],
          includeUncollected: true,
          webCollections: false,
          webCollectionsMode: 'all' as const,
          selectedWebCollectionIds: [],
        };
      }
      return {
        ...prev,
        myLibrary: true,
        webCollections: true,
        selectedCollectionIds: [],
        libraryMode: 'all' as const,
        selectedWebCollectionIds: [],
        webCollectionsMode: 'all' as const,
      };
    });
  }, [setToggleStateGuarded, recordSourceControlUsage]);

  const handlePartnerToggle = useCallback(
    (partnerId: string, context?: PartnerTrackingContext) => {
      recordSourceControlUsage();
      const newState = !(toggleState.integrationPartners[partnerId] ?? false);
      if (isExpandedSourceTrackingEnabled) {
        track({
          event_name: TrackingEventName.ButtonClicked,
          props: {
            ...baseProps,
            action: ButtonAction.EvidenceSourceSelected,
            label: context?.displayName ?? 'Partner toggle',
            is_enabled: newState,
            feature: 'evidence',
          },
        });
      }
      setToggleStateGuarded((prev) => ({
        ...prev,
        integrationPartners: {
          ...prev.integrationPartners,
          [partnerId]: !(prev.integrationPartners[partnerId] ?? false),
        },
      }));
    },
    [
      setToggleStateGuarded,
      recordSourceControlUsage,
      toggleState.integrationPartners,
      isExpandedSourceTrackingEnabled,
      baseProps,
    ]
  );

  const handleClinicalSourcesToggle = useCallback(
    (checked: boolean) => {
      recordSourceControlUsage();
      if (isExpandedSourceTrackingEnabled) {
        track({
          event_name: TrackingEventName.ButtonClicked,
          props: {
            ...baseProps,
            action: ButtonAction.EvidenceLocalGuidelinesClicked,
            label: 'Guidelines only',
            is_enabled: checked,
            feature: 'evidence',
          },
        });
      }
      setToggleStateGuarded((prev) => ({
        ...prev,
        localGuidelines: checked,
        primaryEvidence: checked,
      }));
    },
    [
      setToggleStateGuarded,
      recordSourceControlUsage,
      isExpandedSourceTrackingEnabled,
      baseProps,
    ]
  );

  /**
   * Unified collection toggle for IS_UNIFIED_COLLECTIONS_ENABLED.
   * Performs an atomic state update that checks BOTH buckets to handle
   * the case where a collection's _source changes between selections
   * (e.g., user edits domains then returns to chat).
   */
  const handleUnifiedCollectionToggle = useCallback(
    (
      collectionId: string,
      source: 'library' | 'web',
      context?: CollectionTrackingContext
    ) => {
      recordSourceControlUsage();
      const isCurrentlySelected =
        toggleState.selectedCollectionIds.includes(collectionId) ||
        toggleState.selectedWebCollectionIds.includes(collectionId);
      trackCollectionToggle(
        !isCurrentlySelected,
        context,
        source === 'web' ? 'Web collection' : 'Library collection'
      );
      setToggleStateGuarded((prev) => {
        const inDoc = prev.selectedCollectionIds.includes(collectionId);
        const inWeb = prev.selectedWebCollectionIds.includes(collectionId);
        const currentlySelected = inDoc || inWeb;

        if (currentlySelected) {
          // Deselect: remove from BOTH buckets to avoid stale ghost entries
          const newDocIds = inDoc
            ? prev.selectedCollectionIds.filter((id) => id !== collectionId)
            : prev.selectedCollectionIds;
          const newWebIds = inWeb
            ? prev.selectedWebCollectionIds.filter((id) => id !== collectionId)
            : prev.selectedWebCollectionIds;

          return {
            ...prev,
            selectedCollectionIds: newDocIds,
            selectedWebCollectionIds: newWebIds,
            // Disable category when last ID removed
            ...(newDocIds.length === 0 && inDoc && prev.myLibrary
              ? { myLibrary: false, libraryMode: 'all' as const, includeUncollected: false }
              : {}),
            ...(newWebIds.length === 0 && inWeb && prev.webCollections
              ? { webCollections: false, webCollectionsMode: 'all' as const }
              : {}),
          };
        }

        // Select: add to the correct bucket based on current _source
        if (source === 'web') {
          const newWebIds = [...prev.selectedWebCollectionIds, collectionId];
          return {
            ...prev,
            selectedWebCollectionIds: newWebIds,
            webCollectionsMode: 'collections' as const,
            webCollections: true,
          };
        }

        const newDocIds = [...prev.selectedCollectionIds, collectionId];
        return {
          ...prev,
          selectedCollectionIds: newDocIds,
          libraryMode: 'collections' as const,
          myLibrary: true,
        };
      });
    },
    [
      setToggleStateGuarded,
      recordSourceControlUsage,
      toggleState.selectedCollectionIds,
      toggleState.selectedWebCollectionIds,
      trackCollectionToggle,
    ]
  );

  const resetUncollected = useCallback(() => {
    setToggleState((prev) =>
      prev.includeUncollected ? { ...prev, includeUncollected: false } : prev
    );
  }, [setToggleState]);

  return {
    toggleState,
    handleDocCollectionToggle,
    handleWebCollectionToggle,
    handleUnifiedCollectionToggle,
    handleUncollectedToggle,
    handleAllLibraryToggle,
    isAllLibrarySelected,
    handlePartnerToggle,
    handleClinicalSourcesToggle,
    resetUncollected,
    isRequireAtLeastOneSource,
  };
};
